/**
 * SubscriptionBanner — app-shell lifecycle banner (V3 Phase 3).
 *   expired    → rose, persistent: read-only (view + export only)
 *   grace      → amber, persistent: 7-day grace with full access
 *   downgrade  → violet: scheduled downgrade + what must be reduced
 *   reminder   → 15 / 7 / 1 days before trial / plan end (dismissible per stage)
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { AlertTriangle, ShieldX, Clock, ArrowRight, X, BellRing } from 'lucide-react';
import { useSubscription } from '../SubscriptionContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;
const TONES = {
  rose: ['bg-rose-50 border-rose-300 text-rose-900', 'bg-rose-600 hover:bg-rose-700'],
  amber: ['bg-amber-50 border-amber-300 text-amber-900', 'bg-amber-600 hover:bg-amber-700'],
  violet: ['bg-violet-50 border-violet-300 text-violet-900', 'bg-violet-600 hover:bg-violet-700'],
  indigo: ['bg-indigo-50 border-indigo-200 text-indigo-900', 'bg-indigo-600 hover:bg-indigo-700'],
};

const reminderStage = (d) => (d == null || d < 0 ? null : [1, 7, 15].find((s) => d <= s) || null);

function useDowngradeStatus(scheduled) {
  const [st, setSt] = useState(null);
  useEffect(() => {
    if (!scheduled) { setSt(null); return; }
    axios.get(`${API}/subscriptions/downgrade-status`).then((r) => setSt(r.data)).catch(() => setSt(null));
  }, [scheduled?.to, scheduled?.at]); // eslint-disable-line react-hooks/exhaustive-deps
  return st;
}

function useLimitsCheck(v2) {
  const [c, setC] = useState(null);
  const want = !!v2.legacy_plan && v2.days_left != null && v2.days_left <= 30;
  useEffect(() => {
    if (!want) { setC(null); return; }
    axios.get(`${API}/subscriptions/renewal-limits-check`).then((r) => setC(r.data)).catch(() => setC(null));
  }, [want, v2.current_period_end]);
  return c;
}

function pickBanner(v2, dg, lc) {
  const d = v2.days_left;
  if (v2.status === 'expired') {
    return { tone: 'rose', Icon: ShieldX, cta: 'Renew', persistent: true,
      text: <><b>Your subscription has expired — read-only mode.</b> You can view and export all your data, but can't create new patients, appointments, invoices or tests until you renew.</> };
  }
  if (v2.status === 'grace') {
    return { tone: 'amber', Icon: AlertTriangle, cta: 'Renew now', persistent: true,
      text: <><b>Your plan has expired — {plural(d ?? 0, 'day')} of grace left.</b> Everything works until {fmtDate(v2.grace_ends_at)}; after that your clinic becomes read-only.</> };
  }
  if (v2.downgrade_scheduled) {
    const red = dg?.reduction;
    const blocked = red && !red.fits;
    return { tone: 'violet', Icon: Clock, cta: 'Manage', persistent: blocked, key: `dg:${v2.downgrade_scheduled.at}`,
      text: <><b>Downgrade to {v2.downgrade_scheduled.to} on {fmtDate(v2.downgrade_scheduled.at)}.</b>{' '}
        {blocked ? red.messages.join(' ') : 'You keep your current plan until then.'}
        {blocked && v2.downgrade_scheduled.blocked && ' Your downgrade is waiting; you\'ll renew on your current plan until then.'}</> };
  }
  if (lc?.applies) {
    const up = lc.upgrade_option?.tier === 'chain'
      ? `upgrade to Chain (₹${lc.upgrade_option.monthly_price}/mo · ${lc.upgrade_option.included_locations} locations · ${lc.upgrade_option.included_users} users)`
      : 'talk to us about Enterprise';
    return { tone: 'amber', Icon: AlertTriangle, cta: 'Review plan', persistent: true, key: 'limits',
      text: <><b>On {fmtDate(lc.renewal_at)} you move to the new {lc.tier} plan ({lc.new_location_limit} location{lc.new_location_limit === 1 ? '' : 's'} · {lc.new_user_limit} users).</b>{' '}
        {lc.reduction.messages.join(' ')} Options: deactivate extras, add users at ₹{lc.extra_user_price}/mo, add locations at ₹{lc.extra_location_price}/mo, or {up}.</> };
  }
  const stage = ['active', 'trialing', 'cancelled'].includes(v2.status) ? reminderStage(d) : null;
  if (stage) {
    const what = v2.status === 'trialing' ? 'Your free trial ends' : v2.status === 'cancelled' ? 'Your access ends' : 'Your plan expires';
    return { tone: stage === 1 ? 'amber' : 'indigo', Icon: BellRing, cta: 'Renew now', key: `rem:${stage}:${v2.current_period_end}`,
      text: <><b>{what} {d <= 1 ? 'tomorrow' : `in ${d} days`}</b> ({fmtDate(v2.current_period_end)}). Renew to avoid interruption.</> };
  }
  return null;
}

export default function SubscriptionBanner() {
  const sub = useSubscription();
  const v2 = sub?.v2 || {};
  const dg = useDowngradeStatus(v2.downgrade_scheduled);
  const lc = useLimitsCheck(v2);
  const b = pickBanner(v2, dg, lc);
  const storeKey = b?.key ? `audinexa.sub_banner.${b.key}` : null;
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => { setDismissed(!!storeKey && localStorage.getItem(storeKey) === '1'); }, [storeKey]);

  if (!b || (!b.persistent && dismissed)) return null;
  const [wrap, btn] = TONES[b.tone];
  const dismiss = () => { if (storeKey) localStorage.setItem(storeKey, '1'); setDismissed(true); };
  return (
    <div data-testid="subscription-banner" data-status={v2.status} data-kind={b.key ? b.key.split(':')[0] : v2.status}
      className={`${wrap} border-b px-4 py-2.5 flex items-center gap-3 text-[13px]`}>
      <b.Icon size={18} className="shrink-0" />
      <div className="flex-1 leading-snug" data-testid="subscription-banner-text">{b.text}</div>
      <Link to="/billing/my-subscription" data-testid="subscription-banner-cta"
        className={`${btn} inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-bold rounded shadow-sm text-white transition-colors`}>
        {b.cta} <ArrowRight size={13} />
      </Link>
      {!b.persistent && (
        <button onClick={dismiss} data-testid="subscription-banner-dismiss" aria-label="Dismiss" className="opacity-60 hover:opacity-100 p-1">
          <X size={15} />
        </button>
      )}
    </div>
  );
}
